import { computed, onMounted, ref, type ComputedRef, type Ref } from 'vue'

import { useI18n } from '@/app/i18n'
import { switchAccount, type OtherAccount } from '@/app/session'
import { getMyAccounts } from '@/app/session/api'
import { useAuthStore } from '@/app/stores/auth'
import type { UserType } from '@/app/stores/session'
import { errorMessage, toast } from '@/shared/composables/useToast'

export interface AccountOption {
  id: string
  userId: number
  userType: UserType
  label: string
  icon: string
}

export interface AccountSwitch {
  /** The user's other accounts with the same login; empty while loading or without any. */
  options: ComputedRef<AccountOption[]>
  /** The current account's status label ("Business owner", …). */
  currentLabel: ComputedRef<string>
  currentIcon: ComputedRef<string>
  /** Starts the switch: opens "Confirm your identity" for that account. */
  open: (userId: number) => void
  confirming: Ref<boolean>
  password: Ref<string>
  error: Ref<string>
  /** The modal's OK action: switches, or keeps the modal open with the API's message. */
  submit: () => Promise<boolean>
}

/**
 * `users/myAccounts` once per session — the navbar and the nav's account card both list the accounts — keyed by the
 * session token: `logout()` does not reload the page, so the next login on the same tab must not see the previous
 * user's accounts.
 */
let cache: { key: string; request: Promise<OtherAccount[]> } | null = null
function loadAccounts(key: string): Promise<OtherAccount[]> {
  if (cache?.key !== key) {
    const request = getMyAccounts()
      .then((response) => response.data)
      .catch(() => {
        if (cache?.request === request) cache = null
        return []
      })
    cache = { key, request }
  }
  return cache.request
}

/**
 * "Switch current role" (wp/shell §4.16, §4.16b): the user's other accounts with the same login, the status label of
 * each, and the password confirmation ("Confirm your identity") that `users/toggleAccount` needs before the reload on
 * `/`. An account using 2FA is refused by the API with its own message (FX-044, for the owner). Shared by the navbar's
 * role switcher (below 1200 px) and the vertical nav's account card.
 */
export function useAccountSwitch(): AccountSwitch {
  const { t } = useI18n()
  const auth = useAuthStore()
  const accounts = ref<OtherAccount[]>([])
  const target = ref<OtherAccount | null>(null)
  const confirming = ref(false)
  const password = ref('')
  const error = ref('')

  onMounted(async () => {
    const key = auth.session?.token
    accounts.value = key ? await loadAccounts(key) : []
  })

  const LABELS: Record<UserType, () => string> = {
    Freelancer: () => t('shell.navbar.status.freelancer'),
    Client: () => t('shell.navbar.status.client'),
    Staff: () => t('shell.navbar.status.staff'),
    User: () => '',
  }
  const ICONS: Record<UserType, string> = {
    Freelancer: 'UserIcon',
    Client: 'BriefcaseIcon',
    Staff: 'UsersIcon',
    User: 'UserIcon',
  }

  const options = computed<AccountOption[]>(() =>
    accounts.value.map((account) => ({
      id: `account-${account.user_id}`,
      userId: account.user_id,
      userType: account.user_type,
      label: LABELS[account.user_type](),
      icon: ICONS[account.user_type],
    })),
  )
  const currentLabel = computed(() => LABELS[auth.userType ?? 'Client']())
  const currentIcon = computed(() => ICONS[auth.userType ?? 'Client'])

  function open(userId: number): void {
    target.value = accounts.value.find((account) => account.user_id === userId) ?? null
    if (!target.value) return
    password.value = ''
    error.value = ''
    confirming.value = true
  }

  async function submit(): Promise<boolean> {
    if (!password.value) {
      error.value = t('common.validation.required', { field: t('shell.modal.confirmIdentity.field.password') })
      return false
    }
    if (!target.value) return false
    try {
      const message = await switchAccount(target.value.user_id, password.value)
      if (message) toast.success(message)
      return true
    } catch (failure) {
      // The password stays (legacy); the modal stays open.
      toast.danger(errorMessage(failure))
      return false
    }
  }

  return { options, currentLabel, currentIcon, open, confirming, password, error, submit }
}
